import { Form } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { FormContainer } from '../common/form/FormContainer';
import { SubmitButton } from '../common/form/SubmitButton';
import { SectionHeader } from '../common/SectionHeader';
import { Modal as QcsModal } from '@mui/material';

import { QcsModalBox } from '../common/basic/QcsModalBox';
import { LanguageSelect } from '../common/form/LanguageSelect';
import { FetchState } from '../../store/fetchState';
import { useAppDispatch, useAppSelector } from '../../store';
import { getAppTrainingLangList, selectAppTrainingLangList } from '../../store/entities/appTrainingLangList';
import { selectQuestionnaireVersionLanguages } from '../../store/entities/questionnaireVersion';
import {
  getCompanyCustomization,
  selectCompanyCustomizationState,
  selectCompanyCustomizationTrainingLangCodes,
} from '../../store/entities/companyCustomization';
import { selectIdentityCompanyId } from '../../store/entities/identity';
import { LanguageDto } from '@api/models/settings/LanguageDto';
import { AddLanguageVariant } from '@api/models/training/AddLanguageVariant';
import { questionnaireVersionService } from '@api/services/services';
import { ShowError } from '../ShowError';
import { resources } from '@api/resources';

interface AddLanguageForm {
  languageToCopy: string;
  language?: string;
}

interface Props {
  open: boolean;
  languageToCopy?: LanguageDto | null;
  onClose: () => void;
  reloadQuestionnaireVersion: () => void;
}

export const AddLanguageModal: FC<Props> = ({ open, languageToCopy, onClose, reloadQuestionnaireVersion }) => {
  const { t } = useTranslation();
  const { questionnaireVersionId } = useParams();
  const dispatch = useAppDispatch();
  const appTrainingLangList = useAppSelector(selectAppTrainingLangList);
  const identityCompanyId = useAppSelector(selectIdentityCompanyId);
  const companyCustomizationState = useAppSelector(selectCompanyCustomizationState);
  const companyCustomizationTrainingLangCodes = useAppSelector(selectCompanyCustomizationTrainingLangCodes);
  const questionnaireVersionLanguages = useAppSelector(selectQuestionnaireVersionLanguages);

  useEffect(() => {
    if (companyCustomizationState === FetchState.None) {
      dispatch(getCompanyCustomization());
    }
    if (appTrainingLangList.state === FetchState.None) {
      dispatch(getAppTrainingLangList());
    }
  }, [appTrainingLangList.state, companyCustomizationState, dispatch, identityCompanyId]);

  const handleSubmit = async (formData: AddLanguageForm) => {
    const data: AddLanguageVariant = {
      language: appTrainingLangList.data?.content.find((x) => x.code === formData.language) ?? { code: '', flag: '', name: '' },
    };

    if (languageToCopy) {
      await questionnaireVersionService.createCopyFromLanguageVersion(questionnaireVersionId!, languageToCopy.code, data);
    } else {
      await questionnaireVersionService.addLanguageForVersion(questionnaireVersionId!, data);
    }

    reloadQuestionnaireVersion();
    onClose();
  };

  const questionnaireLanguages = useMemo(
    () => appTrainingLangList.data?.content.filter((x) => companyCustomizationTrainingLangCodes?.includes(x.code)),
    [appTrainingLangList.data?.content, companyCustomizationTrainingLangCodes],
  );

  return (
    <QcsModal open={open} onClose={onClose}>
      <QcsModalBox>
        <SectionHeader title={languageToCopy ? 'questionnaire.copy.addLanguage' : 'questionnaire.addLanguage'} />
        <FormikDebounce<AddLanguageForm>
          initialValues={{
            language: '',
            languageToCopy: languageToCopy?.code ?? '',
          }}
          validationSchema={Yup.object().shape({
            language: validations.stringRequired(t),
          })}
          onSubmit={handleSubmit}
        >
          <Form>
            <FormContainer>
              {!!languageToCopy && (
                <LanguageSelect name="languageToCopy" label={t('questionnaire.copy.languageToCopy')} languages={questionnaireLanguages} disabled />
              )}
              <LanguageSelect
                name="language"
                label={t('questionnaire.language')}
                languages={questionnaireLanguages}
                notAllowedLanguages={questionnaireVersionLanguages?.map((language) => language.code)}
                required
              />

              <ShowError resource={[resources.addLanguageForVersion, resources.createCopyFromLanguageVersion]} />
              <SubmitButton />
            </FormContainer>
          </Form>
        </FormikDebounce>
      </QcsModalBox>
    </QcsModal>
  );
};
